import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {sum, isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import {getPercent} from '@ey/utils';
import {labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import normalizeDashboardFilters from '../../../../../../util/dashboard/normalizeDashboardFilters';
import {numberFormatsListItems, GARoleTypeId, dashboardTabId} from '../../../../../../util/uiconstants';
import SummaryBarChart from '../../../../../Common/Charts/SummaryBarChart';
import {
	clearFilterDashboardTiles,
	updateFilterDashboardTiles
} from '../../../../../../actions/Metrics/dashboardFiltersAction';
import summaryStatusTileDataConfig from './summaryStatusTileDataConfig';

const PrimaryTeamSummaryStatus = ({engagementMetrics}) => {
	const dispatch = useDispatch();
	const [summaryStatusTileData, setSummaryStatusTileData] = useState({});
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);

	useEffect(() => {
		if (!isEmpty(engagementMetrics)) {
			let _engagementMetrics = engagementMetrics?.filter(
				(x) => x?.gAType === GARoleTypeId.Primary || x?.gAType === GARoleTypeId.Region
			);

			// sum all values to get the total for the selected engagements
			const total = sum(_engagementMetrics.map((item) => item.pTTotal));
			const taskOpenOverdue = sum(_engagementMetrics.map((item) => item.pTOD));
			const taskOpen = sum(_engagementMetrics.map((item) => item.pTOpen));
			const taskInProgress = sum(_engagementMetrics.map((item) => item.pTInProg));
			const taskInReview = sum(_engagementMetrics.map((item) => item.pTInRev));
			const taskComplete = sum(_engagementMetrics.map((item) => item.pTCompl));

			const [
				{percentage: taskOpenPercent},
				{percentage: taskInProgressPercent},
				{percentage: taskInReviewPercent},
				{percentage: taskCompletePercent}
			] = Utility.adjustPercentages([
				{percentage: getPercent(taskOpen, total)},
				{percentage: getPercent(taskInProgress, total)},
				{percentage: getPercent(taskInReview, total)},
				{percentage: getPercent(taskComplete, total)}
			]);
			const formatId = numberFormat && numberFormatsListItems.find((x) => x.label == numberFormat)?.id;

			setSummaryStatusTileData(
				summaryStatusTileDataConfig(
					formatId,
					total,
					taskOpen,
					taskOpenOverdue,
					numberFormat,
					taskOpenPercent,
					taskInProgress,
					taskInProgressPercent,
					taskInReview,
					taskInReviewPercent,
					taskComplete,
					taskCompletePercent
				)
			);
		} else {
			setSummaryStatusTileData({});
		}
	}, [engagementMetrics]);

	const onGraphicClickHandler = (serieLabel) => {
		updateFilters(serieLabel);
	};

	const handlerOnClearFilter = () => dispatch(clearFilterDashboardTiles(dashboardTabId.groupAudit));

	const updateFilters = (serieLabel) => {
		const filter = [{categoryLabel: labels.reportingForm, serieLabel: serieLabel, onClearChip: handlerOnClearFilter}];
		dispatch(
			updateFilterDashboardTiles(
				dashboardTabId.groupAudit,
				normalizeDashboardFilters(
					filter,
					engagementMetrics?.filter((eng) => filterEngagementByChips(eng, filter)).map((e) => e.engId) ?? [],
					handlerOnClearFilter
				)
			)
		);
	};

	const filterEngagementByChips = (engagement, filter) => {
		if (!engagement || _.isEmpty(engagement)) return false;
		return filter.some(({serieLabel}) => {
			return evalReportingFromStatus(engagement, serieLabel);
		});
	};

	const evalReportingFromStatus = (engagement, serieLabel) => {
		const {pTOpen, pTInProg, pTInRev, pTCompl, pTOD} = engagement;
		switch (serieLabel) {
			case labels.open:
				return pTOpen;
			case labels.inProgress:
				return pTInProg;
			case labels.inReview:
				return pTInRev;
			case labels.completeUpperCase:
				return pTCompl;
			case `${labels.open} ${labels.overdue}`:
				return pTOD;
		}
	};

	return (
		<SummaryBarChart
			chartsConfig={summaryStatusTileData.chartsData}
			count={summaryStatusTileData.total}
			numberFormat={numberFormat}
			title={labels.reportingFormStatus}
			secondTitle={labels.summarizedForPrimaryTeams}
			subtitle={labels.primaryTileSubtitle}
			noDataTitle={labels.thereIsNoDataToDisplay}
			noDataSubtitle={labels.selectadditionalEntitiesToContinue}
			onGraphicClick={onGraphicClickHandler}
			onOverdueClick={onGraphicClickHandler}
			filterKey={labels.reportingFormStatus}
		/>
	);
};

PrimaryTeamSummaryStatus.defaultProps = {
	engagementMetrics: []
};

PrimaryTeamSummaryStatus.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default PrimaryTeamSummaryStatus;
